/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import { Icons, NumericInput, PLACEMENT } from '@web-stories-wp/design-system';

/**
 * External dependencies
 */
import { __, _x, sprintf } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import { OverlayPreset, OverlayType } from '../../../../utils/overlay';
import { MULTIPLE_VALUE } from '../../../../constants';
import { SimplePanel } from '../../panel';
import { inputContainerStyleOverride } from '../../shared';
import Popup from '../../../popup';
import ColorPicker from '../../../colorPicker';
import { INPUT_GAP } from '../../../colorPicker/constants';
import OpacityInput from '../../../form/color/opacityInput';
import applyOpacityChange from '../../../form/color/applyOpacityChange';
import useInspector from '../../../inspector/useInspector';
import { useCanvas } from '../../../../app';
import { useTransform } from '../../../transform';
import {
  getLegacyOverlayFromStack,
  getOverlayFilterStyle,
  getOverlayStack,
} from '../../../../utils/overlayStack';
import { noop } from '../../../../utils/noop';
import convertOverlay from './convertOverlay';

const ADD_FILTER_TYPES = [
  OverlayType.SOLID,
  OverlayType.LINEAR,
  OverlayType.RADIAL,
];
const PICKER_SPACING = { x: 20 };
const FILTER_BLUR_MIN = 0;
const FILTER_BLUR_MAX = 100;
const FILTER_GRADIENT_INDICATOR_SELECTOR = '[data-filter-gradient-indicator]';

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const FilterRow = styled.div`
  align-items: center;
  background: #1e1f20;
  border: 1px solid
    ${({ $isDragTarget, $isSelected, theme }) =>
      $isDragTarget || $isSelected
        ? theme.colors.border.defaultActive
        : '#313236'};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.fg.primary};
  display: flex;
  gap: 10px;
  min-height: 50px;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.65 : 1)};
  padding: 8px 8px 8px 12px;
  width: 100%;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }
`;

const DragHandle = styled.button`
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.colors.fg.secondary};
  cursor: grab;
  display: block;
  flex: 0 0 12px;
  height: 24px;
  opacity: 0.9;
  padding: 0;
  width: 12px;

  &:active {
    cursor: grabbing;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 3px;
  }

  &::before {
    background-image: radial-gradient(currentColor 1.5px, transparent 1.5px);
    background-size: 6px 6px;
    content: '';
    display: block;
    height: 24px;
    width: 12px;
  }
`;

const SwatchPreview = styled.span`
  background: linear-gradient(135deg, #29859e 0%, #f59e4d 100%);
  border-radius: 5px;
  display: block;
  flex: 0 0 32px;
  height: 32px;
  overflow: hidden;
  position: relative;
  width: 32px;
`;

const SwatchOverlay = styled.span`
  bottom: 0;
  display: block;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const FilterName = styled.span`
  color: #e4e5e6;
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FilterEditButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex: 1 1 auto;
  gap: 8px;
  min-width: 0;
  padding: 0;
  text-align: left;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 3px;
  }
`;

const FilterActions = styled.span`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
`;

const ActionButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: #9ba1a3;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 24px;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.fg.primary};
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 1px;
  }

  svg {
    height: 20px;
    width: 20px;
  }
`;

const RemoveGlyph = styled.span`
  background: currentColor;
  border-radius: 2px;
  display: block;
  height: 2px;
  width: 12px;
`;

const AddFilterButton = styled.button`
  align-items: center;
  background: #393d3f;
  border: 0;
  border-radius: 6px;
  color: #e4e5e6;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  gap: 6px;
  justify-content: center;
  line-height: 20px;
  min-height: 32px;
  padding: 5px 10px;
  width: 100%;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.interactiveBg.secondaryHover};
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }

  svg {
    height: 22px;
    width: 22px;
  }
`;

const AddFilterChoices = styled.div`
  align-items: flex-start;
  background: #1e1f20;
  border: 1px solid #313236;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  min-height: 93px;
  padding: 16px;
  width: 100%;
`;

const AddFilterChoiceButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: #e4e5e6;
  cursor: pointer;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 0;

  &:hover,
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 3px;
  }
`;

const ChoicePreview = styled.span`
  background: ${({ $type }) => getFilterChoicePreview($type)};
  border-radius: 5px;
  display: block;
  height: 43px;
  width: 54px;
`;

const ChoiceLabel = styled.span`
  color: #e4e5e6;
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const FilterOpacityInputWrapper = styled.div`
  display: block;
  flex: 0 0 52px;
  width: 52px;
`;

const FilterOpacityInput = styled(OpacityInput)`
  min-width: 52px;
  width: 52px;

  > div {
    height: 28px;
    padding: 3px 6px;
  }

  input {
    color: #919899;
    font-size: 14px;
    line-height: 20px;
    text-align: right;
  }
`;

const FilterBlurControl = styled.div`
  margin-bottom: ${INPUT_GAP}px;
`;

const FilterBlurInput = styled(NumericInput)`
  width: 100%;
`;

function getOverlayType(pattern) {
  if (!pattern) {
    return OverlayType.NONE;
  }
  return pattern.type || OverlayType.SOLID;
}

function getOverlayTypeLabel(type) {
  if (type === OverlayType.RADIAL) {
    return __('Radial', 'web-stories');
  }

  return OverlayPreset[type]?.label || __('Filter', 'web-stories');
}

function getFilterChoicePreview(type) {
  const sampleImage = 'linear-gradient(135deg, #29859e 0%, #f59e4d 100%)';

  if (type === OverlayType.RADIAL) {
    return `radial-gradient(circle at center, rgba(0, 0, 0, 0) 28%, rgba(0, 0, 0, 0.66) 100%), ${sampleImage}`;
  }

  if (type === OverlayType.LINEAR) {
    return `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(0, 0, 0, 0.62) 100%), ${sampleImage}`;
  }

  return `linear-gradient(90deg, rgba(26, 13, 89, 0.42), rgba(26, 13, 89, 0.42)), ${sampleImage}`;
}

function preserveLinearEndpointGeometry(currentPattern, nextPattern) {
  if (
    nextPattern?.geometryVersion === 2 ||
    currentPattern?.type !== OverlayType.LINEAR ||
    nextPattern?.type !== OverlayType.LINEAR ||
    !currentPattern.start ||
    !currentPattern.end ||
    (currentPattern.rotation || 0) !== (nextPattern.rotation || 0)
  ) {
    return nextPattern;
  }

  return {
    ...nextPattern,
    start: currentPattern.start,
    end: currentPattern.end,
  };
}

function getFilterLabel(filter) {
  return getOverlayTypeLabel(getOverlayType(filter.pattern));
}

function getComparableOverlayStack(element) {
  return getOverlayStack(element).map(({ id, ...filter }) => filter);
}

function getCommonOverlayStack(elements) {
  if (!elements.length) {
    return '[]';
  }

  const firstComparableStack = JSON.stringify(
    getComparableOverlayStack(elements[0])
  );
  const allMatch = elements.every(
    (element) =>
      JSON.stringify(getComparableOverlayStack(element)) ===
      firstComparableStack
  );

  return allMatch
    ? JSON.stringify(getOverlayStack(elements[0]))
    : MULTIPLE_VALUE;
}

function getFilterInsertPosition(evt) {
  const targetRect = evt?.currentTarget?.getBoundingClientRect?.();
  if (!targetRect?.height || !Number.isFinite(evt?.clientY)) {
    return null;
  }

  return evt.clientY < targetRect.top + targetRect.height / 2
    ? 'before'
    : 'after';
}

function reorderFilters(filters, sourceId, targetId, insertPosition) {
  if (!sourceId || !targetId || sourceId === targetId) {
    return filters;
  }

  const sourceIndex = filters.findIndex(({ id }) => id === sourceId);
  const targetIndex = filters.findIndex(({ id }) => id === targetId);
  if (sourceIndex < 0 || targetIndex < 0) {
    return filters;
  }

  const nextFilters = filters.filter(({ id }) => id !== sourceId);
  const targetInsertionIndex = nextFilters.findIndex(
    ({ id }) => id === targetId
  );
  const shouldInsertAfter =
    insertPosition === 'after' ||
    (!insertPosition && sourceIndex < targetIndex);
  nextFilters.splice(
    targetInsertionIndex + (shouldInsertAfter ? 1 : 0),
    0,
    filters[sourceIndex]
  );

  return hasSameFilterOrder(filters, nextFilters) ? filters : nextFilters;
}

function hasSameFilterOrder(filtersA, filtersB) {
  return (
    filtersA.length === filtersB.length &&
    filtersA.every(({ id }, index) => id === filtersB[index]?.id)
  );
}

function FilterPanel({ selectedElements, pushUpdate }) {
  const pickerAnchor = useRef(null);
  const filterRows = useRef({});
  const handledFilterPickerRequestId = useRef(null);
  const {
    refs: { inspector },
  } = useInspector();
  const {
    clearFilterPickerRequest,
    filterPickerRequest,
    isEyedropperActive,
    setActiveFilterId,
  } = useCanvas(
    ({
      state: { filterPickerRequest, isEyedropperActive },
      actions: {
        clearFilterPickerRequest = noop,
        setActiveFilterId = noop,
      } = {},
    }) => ({
      clearFilterPickerRequest,
      filterPickerRequest,
      isEyedropperActive,
      setActiveFilterId,
    })
  );
  const {
    actions: { pushTransform },
  } = useTransform();
  const commonStack = getCommonOverlayStack(selectedElements);
  const isMultiple = commonStack === MULTIPLE_VALUE;
  const filters = useMemo(
    () => (isMultiple ? [] : JSON.parse(commonStack)),
    [commonStack, isMultiple]
  );
  const selectedElementIdsKey = useMemo(
    () => selectedElements.map(({ id }) => id).join(','),
    [selectedElements]
  );
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [isAddingFilter, setIsAddingFilter] = useState(false);
  const [draggedFilterId, setDraggedFilterId] = useState(null);
  const [dragTargetFilterId, setDragTargetFilterId] = useState(null);
  const [dragPreviewFilters, setDragPreviewFilters] = useState(null);
  const [activeFilterPickerId, setActiveFilterPickerId] = useState(null);
  const [isEditingFilterColor, setIsEditingFilterColor] = useState(false);
  const [pickerAnchorElement, setPickerAnchorElement] = useState(null);
  const displayFilters = dragPreviewFilters || filters;
  const selectedFilter = displayFilters.find(
    ({ id }) => id === selectedFilterId
  );
  const activeFilter = filters.find(({ id }) => id === activeFilterPickerId);
  pickerAnchor.current = pickerAnchorElement;

  useEffect(() => {
    setSelectedFilterId(null);
    setActiveFilterPickerId(null);
    setPickerAnchorElement(null);
  }, [selectedElementIdsKey]);

  useEffect(() => {
    setActiveFilterId(isEditingFilterColor ? activeFilter?.id || null : null);
  }, [activeFilter?.id, isEditingFilterColor, setActiveFilterId]);

  useEffect(() => {
    return () => setActiveFilterId(null);
  }, [setActiveFilterId]);

  const updateFilters = useCallback(
    (nextFilters) =>
      pushUpdate(
        {
          overlay: getLegacyOverlayFromStack(nextFilters),
          overlays: nextFilters,
        },
        true
      ),
    [pushUpdate]
  );

  const addFilter = useCallback(
    (type) => {
      if (type === OverlayType.NONE) {
        setSelectedFilterId(null);
        setIsAddingFilter(false);
        updateFilters([]);
        return;
      }

      const pattern = convertOverlay(null, OverlayType.NONE, type);
      const nextFilter = {
        id: uuidv4(),
        pattern,
      };
      setSelectedFilterId(nextFilter.id);
      setIsAddingFilter(false);
      updateFilters([nextFilter, ...filters]);
    },
    [filters, updateFilters]
  );

  const updateFilter = useCallback(
    (id, updates) => {
      updateFilters(
        filters.map((filter) =>
          filter.id === id ? { ...filter, ...updates } : filter
        )
      );
    },
    [filters, updateFilters]
  );

  const previewFilterPattern = useCallback(
    (filter, pattern) => {
      if (!filter || !pattern) {
        return;
      }

      const nextPattern = preserveLinearEndpointGeometry(
        filter.pattern,
        pattern
      );

      selectedElements.forEach(({ id }) => {
        pushTransform(id, {
          color: nextPattern,
          overlayFilterId: filter.id,
          staticTransformation: true,
          style: 'background',
        });
      });
    },
    [pushTransform, selectedElements]
  );

  const updateFilterOpacity = useCallback(
    (id, opacity) => {
      const filter = filters.find(({ id: filterId }) => filterId === id);
      if (!filter) {
        return;
      }

      updateFilter(id, {
        pattern: applyOpacityChange(filter.pattern, opacity),
      });
    },
    [filters, updateFilter]
  );

  const updateFilterBlur = useCallback(
    (id, blur) => {
      updateFilter(id, {
        blur: Math.max(FILTER_BLUR_MIN, Math.min(FILTER_BLUR_MAX, blur || 0)),
      });
    },
    [updateFilter]
  );

  const removeFilter = useCallback(
    (id) => {
      const index = filters.findIndex((filter) => filter.id === id);
      const nextFilters = filters.filter((filter) => filter.id !== id);
      setSelectedFilterId((currentSelectedFilterId) =>
        currentSelectedFilterId === id
          ? nextFilters[Math.max(0, index - 1)]?.id || nextFilters[0]?.id
          : currentSelectedFilterId
      );
      updateFilters(nextFilters);
    },
    [filters, updateFilters]
  );

  const moveFilter = useCallback(
    (id, direction) => {
      const index = filters.findIndex((filter) => filter.id === id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= filters.length) {
        return;
      }
      const nextFilters = [...filters];
      [nextFilters[index], nextFilters[nextIndex]] = [
        nextFilters[nextIndex],
        nextFilters[index],
      ];
      updateFilters(nextFilters);
    },
    [filters, updateFilters]
  );

  const reorderFilter = useCallback(
    (nextFilters, sourceId) => {
      if (
        !sourceId ||
        !nextFilters ||
        hasSameFilterOrder(filters, nextFilters)
      ) {
        return;
      }

      setSelectedFilterId(sourceId);
      updateFilters(nextFilters);
    },
    [filters, updateFilters]
  );

  const previewFilterReorder = useCallback(
    (targetId, evt) => {
      if (!draggedFilterId || draggedFilterId === targetId) {
        return;
      }

      const insertPosition = getFilterInsertPosition(evt);
      if (!insertPosition && dragTargetFilterId === targetId) {
        return;
      }

      setDragTargetFilterId(targetId);
      setDragPreviewFilters((currentFilters) => {
        const currentOrder = currentFilters || filters;
        const nextFilters = reorderFilters(
          currentOrder,
          draggedFilterId,
          targetId,
          insertPosition
        );
        return nextFilters === currentOrder ? currentFilters : nextFilters;
      });
    },
    [dragTargetFilterId, draggedFilterId, filters]
  );

  const openFilterPicker = useCallback((filter, anchor) => {
    setSelectedFilterId(filter.id);
    setPickerAnchorElement(anchor);
    setActiveFilterPickerId(filter.id);
  }, []);

  useEffect(() => {
    if (
      !filterPickerRequest ||
      handledFilterPickerRequestId.current === filterPickerRequest.requestId
    ) {
      return;
    }

    const filter = filters.find(
      ({ id }) => id === filterPickerRequest.filterId
    );

    if (!filter) {
      clearFilterPickerRequest();
      return;
    }

    handledFilterPickerRequestId.current = filterPickerRequest.requestId;
    clearFilterPickerRequest();
    openFilterPicker(
      filter,
      filterRows.current[filter.id] || pickerAnchorElement
    );
  }, [
    clearFilterPickerRequest,
    filterPickerRequest,
    filters,
    openFilterPicker,
    pickerAnchorElement,
  ]);

  const closeFilterPicker = useCallback((evt) => {
    if (evt?.target?.closest?.(FILTER_GRADIENT_INDICATOR_SELECTOR)) {
      return;
    }

    setActiveFilterPickerId(null);
    setPickerAnchorElement(null);
  }, []);

  return (
    <SimplePanel name="filter" title={__('Filters', 'web-stories')}>
      {!isMultiple && (
        <PanelContent>
          {filters.length > 0 && (
            <FilterList>
              {displayFilters.map((filter) => {
                const isSelected = filter.id === selectedFilter?.id;
                const isDragging = filter.id === draggedFilterId;
                const isDragTarget =
                  filter.id === dragTargetFilterId && !isDragging;
                const VisibilityIcon = filter.isHidden
                  ? Icons.VisibilityOff
                  : Icons.Visibility;
                return (
                  <FilterRow
                    key={filter.id}
                    data-filter-row
                    ref={(node) => {
                      if (node) {
                        filterRows.current[filter.id] = node;
                      } else {
                        delete filterRows.current[filter.id];
                      }
                    }}
                    $isDragging={isDragging}
                    $isDragTarget={isDragTarget}
                    $isSelected={isSelected}
                    onDragEnter={(evt) => {
                      if (!draggedFilterId || draggedFilterId === filter.id) {
                        return;
                      }

                      evt.preventDefault();
                      previewFilterReorder(filter.id, evt);
                    }}
                    onDragOver={(evt) => {
                      if (!draggedFilterId) {
                        return;
                      }

                      evt.preventDefault();
                      if (evt.dataTransfer) {
                        evt.dataTransfer.dropEffect = 'move';
                      }
                      previewFilterReorder(filter.id, evt);
                    }}
                    onDrop={(evt) => {
                      if (!draggedFilterId) {
                        return;
                      }

                      evt.preventDefault();
                      evt.stopPropagation();
                      const sourceId =
                        evt.dataTransfer?.getData('text/plain') ||
                        draggedFilterId;
                      reorderFilter(
                        dragPreviewFilters ||
                          reorderFilters(filters, sourceId, filter.id),
                        sourceId
                      );
                      setDraggedFilterId(null);
                      setDragTargetFilterId(null);
                      setDragPreviewFilters(null);
                    }}
                  >
                    <DragHandle
                      aria-label={sprintf(
                        /* translators: %s: Filter type */
                        __('Drag to reorder filter: %s', 'web-stories'),
                        getFilterLabel(filter)
                      )}
                      draggable={filters.length > 1}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        setSelectedFilterId(filter.id);
                      }}
                      onDragStart={(evt) => {
                        evt.stopPropagation();
                        setDraggedFilterId(filter.id);
                        setDragTargetFilterId(filter.id);
                        setDragPreviewFilters(filters);
                        if (evt.dataTransfer) {
                          evt.dataTransfer.effectAllowed = 'move';
                          evt.dataTransfer.setData('text/plain', filter.id);
                          const dragImage =
                            evt.currentTarget.closest('[data-filter-row]');
                          if (dragImage) {
                            evt.dataTransfer.setDragImage?.(
                              dragImage,
                              12,
                              dragImage.getBoundingClientRect().height / 2
                            );
                          }
                        }
                      }}
                      onDragEnd={(evt) => {
                        evt.stopPropagation();
                        setDraggedFilterId(null);
                        setDragTargetFilterId(null);
                        setDragPreviewFilters(null);
                      }}
                      onKeyDown={(evt) => {
                        if (evt.key === 'ArrowUp') {
                          evt.preventDefault();
                          evt.stopPropagation();
                          moveFilter(filter.id, -1);
                        }

                        if (evt.key === 'ArrowDown') {
                          evt.preventDefault();
                          evt.stopPropagation();
                          moveFilter(filter.id, 1);
                        }
                      }}
                      title={__('Drag to reorder filter', 'web-stories')}
                      type="button"
                    />
                    <FilterEditButton
                      aria-label={sprintf(
                        /* translators: %s: Filter type */
                        __('Edit filter: %s', 'web-stories'),
                        getFilterLabel(filter)
                      )}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        openFilterPicker(
                          filter,
                          evt.currentTarget.closest('[data-filter-row]') ||
                            evt.currentTarget
                        );
                      }}
                      onKeyDown={(evt) => {
                        if (evt.shiftKey && evt.key === 'ArrowUp') {
                          evt.preventDefault();
                          moveFilter(filter.id, -1);
                        }

                        if (evt.shiftKey && evt.key === 'ArrowDown') {
                          evt.preventDefault();
                          moveFilter(filter.id, 1);
                        }
                      }}
                      type="button"
                    >
                      <SwatchPreview>
                        <SwatchOverlay style={getOverlayFilterStyle(filter)} />
                      </SwatchPreview>
                      <FilterName>{getFilterLabel(filter)}</FilterName>
                    </FilterEditButton>
                    <FilterOpacityInputWrapper
                      onClick={(evt) => evt.stopPropagation()}
                      onKeyDown={(evt) => evt.stopPropagation()}
                      onPointerDown={(evt) => evt.stopPropagation()}
                    >
                      <FilterOpacityInput
                        label={sprintf(
                          /* translators: %s: Filter type */
                          __('Filter opacity: %s', 'web-stories'),
                          getFilterLabel(filter)
                        )}
                        onChange={(opacity) =>
                          updateFilterOpacity(filter.id, opacity)
                        }
                        suffix=""
                        value={filter.pattern}
                      />
                    </FilterOpacityInputWrapper>
                    <FilterActions>
                      <ActionButton
                        aria-label={
                          filter.isHidden
                            ? __('Show filter', 'web-stories')
                            : __('Hide filter', 'web-stories')
                        }
                        onClick={(evt) => {
                          evt.stopPropagation();
                          updateFilter(filter.id, {
                            isHidden: !filter.isHidden,
                          });
                        }}
                        title={
                          filter.isHidden
                            ? __('Show filter', 'web-stories')
                            : __('Hide filter', 'web-stories')
                        }
                        type="button"
                      >
                        <VisibilityIcon />
                      </ActionButton>
                      <ActionButton
                        aria-label={__('Remove filter', 'web-stories')}
                        onClick={(evt) => {
                          evt.stopPropagation();
                          removeFilter(filter.id);
                        }}
                        title={__('Remove filter', 'web-stories')}
                        type="button"
                      >
                        <RemoveGlyph />
                      </ActionButton>
                    </FilterActions>
                  </FilterRow>
                );
              })}
            </FilterList>
          )}
          <AddFilterButton
            aria-expanded={isAddingFilter}
            aria-label={__('Add filter', 'web-stories')}
            onClick={() => setIsAddingFilter((value) => !value)}
            type="button"
          >
            {__('Add Filter', 'web-stories')}
            <Icons.PlusOutline />
          </AddFilterButton>
          {isAddingFilter && (
            <AddFilterChoices>
              {ADD_FILTER_TYPES.map((type) => {
                const label = getOverlayTypeLabel(type);
                return (
                  <AddFilterChoiceButton
                    key={type}
                    onClick={() => addFilter(type)}
                    type="button"
                  >
                    <ChoicePreview $type={type} aria-hidden />
                    <ChoiceLabel>{label}</ChoiceLabel>
                  </AddFilterChoiceButton>
                );
              })}
            </AddFilterChoices>
          )}
          <Popup
            anchor={pickerAnchor}
            dock={inspector}
            invisible={isEyedropperActive}
            isOpen={Boolean(activeFilter && pickerAnchorElement)}
            placement={PLACEMENT.LEFT_START}
            spacing={PICKER_SPACING}
            renderContents={({ propagateDimensionChange }) => (
              <ColorPicker
                legacyPaint="filter"
                color={activeFilter?.pattern}
                onChange={(value) =>
                  updateFilter(activeFilter.id, {
                    pattern: preserveLinearEndpointGeometry(
                      activeFilter.pattern,
                      value
                    ),
                  })
                }
                allowsGradient
                editColorOnOpen
                onEditingColorChange={setIsEditingFilterColor}
                isEyedropperActive={isEyedropperActive}
                onClose={closeFilterPicker}
                onDimensionChange={propagateDimensionChange}
                onPreviewChange={(value) =>
                  previewFilterPattern(activeFilter, value)
                }
                setOnPickerClose={() => {}}
                debounceLeading={false}
                deferCommitUntilPointerUp
                syncSavedColors={false}
                updateSelectedElements={false}
              >
                <FilterBlurControl>
                  <FilterBlurInput
                    allowEmpty={false}
                    aria-label={sprintf(
                      /* translators: %s: Filter type */
                      __('Filter blur: %s', 'web-stories'),
                      getFilterLabel(activeFilter)
                    )}
                    containerStyleOverride={inputContainerStyleOverride}
                    isFloat={false}
                    min={FILTER_BLUR_MIN}
                    max={FILTER_BLUR_MAX}
                    onBlur={(_evt, value) =>
                      updateFilterBlur(activeFilter.id, value)
                    }
                    onChange={(_evt, value) =>
                      updateFilterBlur(activeFilter.id, value)
                    }
                    suffix={__('Blur', 'web-stories')}
                    unit={_x('px', 'Pixels', 'web-stories')}
                    value={activeFilter.blur || 0}
                  />
                </FilterBlurControl>
              </ColorPicker>
            )}
          />
        </PanelContent>
      )}
    </SimplePanel>
  );
}

FilterPanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default FilterPanel;
